import { useEffect, useMemo, useRef, useState } from "react";
import type { WidgetComponentProps } from "@easyblocks/core";
import { useExternalDataStore } from "../state/external-data-store";

type NewsSearchItem = {
  id: string;
  name?: string;
  title?: string;
  [key: string]: any;
};

function getLabel(item: NewsSearchItem): string {
  return item.name ?? item.title ?? item.id;
}

export function NewsPickerWidget(props: WidgetComponentProps<string>) {
  const instanceIdRef = useRef<string>(`news-picker-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;

  const newsSearchState = useExternalDataStore((state) => state.newsSearch[instanceId]);
  const setNewsSearchQuery = useExternalDataStore((state) => state.setNewsSearchQuery);
  const setNewsSearchDebouncedQuery = useExternalDataStore((state) => state.setNewsSearchDebouncedQuery);
  const searchNews = useExternalDataStore((state) => state.searchNews);

  const query = newsSearchState?.query ?? "";
  const debouncedQuery = newsSearchState?.debouncedQuery ?? "";
  const items = (newsSearchState?.items ?? []) as NewsSearchItem[];
  const isLoading = newsSearchState?.isLoading ?? false;
  const errorText = newsSearchState?.error ?? null;

  const selectedValue = props.id ?? "";
  const stableQuery = useMemo(() => debouncedQuery.trim(), [debouncedQuery]);

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setNewsSearchDebouncedQuery(instanceId, query);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, instanceId, setNewsSearchDebouncedQuery]);

  useEffect(() => {
    const limit = stableQuery ? 50 : 5;
    void searchNews(instanceId, stableQuery, limit);
  }, [instanceId, stableQuery, searchNews]);

  const selectedItem = items.find((it) => it.id === selectedValue);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const itemHeight = 28;
      const containerPadding = 4;
      const allOptions = [{ id: "", label: "" }, ...items];
      const selectedIndex = Math.max(0, allOptions.findIndex((o) => o.id === selectedValue));
      const totalContentHeight = containerPadding * 2 + allOptions.length * itemHeight;
      const maxDropdownHeight = 600;
      const dropdownHeight = Math.min(totalContentHeight, maxDropdownHeight);
      const selectedItemOffset = containerPadding + selectedIndex * itemHeight;
      let top = rect.top - selectedItemOffset;
      const margin = 20;
      top = Math.max(margin, Math.min(window.innerHeight - dropdownHeight - margin, top));
      const dropdownWidth = 180;
      let right = window.innerWidth - rect.right;
      if (right < margin) right = margin;
      if (right > window.innerWidth - dropdownWidth - margin) right = window.innerWidth - dropdownWidth - margin;
      setDropdownPosition({ top, right, width: rect.width });
      requestAnimationFrame(() => {
        if (dropdownRef.current && totalContentHeight > maxDropdownHeight) {
          const scrollTarget = selectedItemOffset - dropdownHeight / 2 + itemHeight / 2;
          dropdownRef.current.scrollTop = Math.max(0, scrollTarget);
        }
      });
    }
  }, [isOpen, items, selectedValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (id: string | null) => {
    props.onChange(id);
    setIsOpen(false);
  };

  const displayLabel = isLoading
    ? "Loading…"
    : selectedItem ? getLabel(selectedItem) : (selectedValue ? "Selected" : "Select item");

  return (
    <div ref={containerRef} className="position-relative w-100" style={{ maxWidth: "100%" }}>
      <input
        type="search"
        placeholder="Search news…"
        value={query}
        onChange={(e) => setNewsSearchQuery(instanceId, e.target.value)}
        style={{
          width: "100%",
          height: "32px",
          border: "1px solid #dee2e6",
          borderRadius: "2px",
          padding: "0 8px",
          fontSize: "12px",
          fontFamily: "system-ui",
          fontWeight: 400,
          lineHeight: "1.15",
          boxSizing: "border-box",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", minHeight: "28px", fontFamily: "system-ui", fontSize: "12px", fontWeight: 400, lineHeight: "1.15", color: "#000", cursor: "default" }}>
          News item
        </div>
        <button
          ref={buttonRef}
          type="button"
          className="border bg-white text-start d-flex align-items-center color-widget-button"
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: "pointer", fontWeight: "normal", fontSize: "12px", width: "120px", height: "32px", borderRadius: "2px", paddingLeft: "8px", paddingRight: "8px" }}
        >
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {displayLabel}
          </span>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="#999999" fillRule="evenodd" clipRule="evenodd"></path>
          </svg>
        </button>
      </div>
      {!isLoading && errorText ? (
        <div style={{ marginTop: "4px", fontSize: "11px", color: "red", fontFamily: "system-ui" }}>Error: {errorText}</div>
      ) : null}
      {isOpen && (
        <div
          ref={dropdownRef}
          role="listbox"
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            minWidth: "120px",
            width: "auto",
            maxWidth: "180px",
            maxHeight: "600px",
            overflowY: "auto",
            overflowX: "hidden",
            zIndex: 10000,
            padding: "4px 0",
            background: "#fff",
            border: "1px solid #E5E5E5",
            borderRadius: "2px",
            boxShadow: "0px 2px 14px 0px rgba(0,0,0,0.15)",
          }}
        >
          <div
            role="option"
            aria-selected={!selectedValue}
            className="native-select-item"
            onClick={() => handleSelect(null)}
            style={{
              display: "flex", alignItems: "center", gap: "6px", boxSizing: "border-box", minWidth: 0,
              minHeight: "28px", padding: !selectedValue ? "3px 6px" : "3px 6px 3px 27px", marginBottom: "4px",
              fontFamily: "system-ui", fontSize: "12px", fontWeight: 400, lineHeight: 1.2,
              color: "#000", cursor: "pointer", outline: "none",
            }}
          >
            {!selectedValue && (
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-wrap">Select item</span>
          </div>
          {items.map((it) => {
            const isSelected = it.id === selectedValue;
            return (
              <div
                key={it.id}
                role="option"
                aria-selected={isSelected}
                className="native-select-item"
                onClick={() => handleSelect(it.id)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px", boxSizing: "border-box", minWidth: 0,
                  minHeight: "28px", padding: isSelected ? "3px 6px" : "3px 6px 3px 27px", marginBottom: "4px",
                  fontFamily: "system-ui", fontSize: "12px", fontWeight: 400, lineHeight: 1.2,
                  color: "#000", cursor: "pointer", outline: "none",
                }}
              >
                {isSelected && (
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                    <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd" />
                  </svg>
                )}
                <span className="text-wrap">{getLabel(it)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default NewsPickerWidget;
